
import img0 from './Images/Create_Custom_Role.png';
  
const content = `# OnData Application Roles

The access to OnData features and privileges are managed through roles and permissions. When you integrate the OnData product with your Identity and Access 
Management (IAM) system to enable Single Sign-On (SSO), the OnData application roles can be assigned to the users or user groups in your IAM system, so that you 
can effectively manage your users' access to the OnData product. For details on how to assign OnData applications roles to users in your IAM system, please refer 
to the examples of [**Assigning Roles In Entra ID**](reference_doc#ondata_portal/sso_integration/entraid_role_assignment) or 
[**Assigning Roles In Okta**](reference_doc#ondata_portal/sso_integration/okta_role_assignment).

OnData comes with a set of default or built-in roles that you may use. You can create your own custom roles to fit your organization needs. The purpose of this document 
is to describe the list of permissions and built-in roles of the OnData application, and illustrate the process to create custom roles.  

## OnData Permissions
Below is the list of permissions available for the OnData product:  
1. Organization and Users - View and manage organization profile, users, SSO configuration, and notification settings  
2. Manage OnData Clients - Add and manage OnData clients, which are managed endpoints for data and file encryption and decryption    
3. Cloud Management - View and manage cloud subscription, IP whitelist and deployment settings, including VPN gateway and network connections  
4. Manage Data Classifications - Add, edit and delete data classifications  
5. Manage Data Access Roles - Add, edit and delete data access roles  
6. Manage Data Owner Groups - Add, edit and delete data owner groups  
7. Key Management - View and manage database encryption keys, token validation keys and custom key vault  
8. Manage Data Access Tokens - Add and delete data access tokens for self and others (all users can view tokens assigned to them by default)  
9. Manage Obfuscation Rules - Add, edit and delete obfuscation rules  
10. Manage Backup Jobs - Add backup jobs, download config files, upload or download databases, and update database validation schedules and backup retention schedules. Schedule backup jobs for hosted databases  
11. Manage Data Catalog - Edit data catalog and file catalog except for data classification, encryption and masking settings  
12. Edit Data Classification and Encryption - Edit data classification, encryption, masking, and obfuscation settings. Manage data info types. Edit file classification  
13. Edit Data Ownership - Edit data ownership  
14. Schedule Encryption and Obfuscation - Schedule encryption and obfuscation jobs. Download encrypted and obfuscated databases  
15. Manage Database Deployments - Deploy a database or delete a deployed database instance  
16. Manage Database Operations - Clone and delete databases, apply encryption setting changes, and configure subsetting for test database generation  
17. Manage File Classification and Protection Rules - Add, edit and delete file classification and protection rules, as well custom info types and document types  
18. Manage SecureFile Settings - View and manage SecureFile settings  
19. Manage SecureAI Settings - View and manage SecureAI settings, including the SecureAI App and the MCP Servers 
  
## OnData Default Roles
Below is the list of default or built-in roles for the OnData product:  
1. System Administrator - A system administrator has all permissions and full access to all features  
2. Security Administrator - A security administrator manages the network and security related features, with the following permissions:  
   * Manage OnData Clients
   * Cloud Management
   * Key Management
   * Manage SecureFile Settings
   * Manage SecureAI Settings
   
3. Data Management Officer - A data management officer manages data classifications, data access roles, data ownership, data catalog and data encryption/obfuscation rules, 
with the following permissions:  	
   * Manage Data Classifications
   * Manage Data Access Roles
   * Manage Data Owner Groups
   * Manage Obfuscation Rules
   * Manage Data Catalog
   * Edit Data Classification and Encryption
   * Edit Data Ownership
   * Manage Data Access Tokens
   * Manage File Classification and Protection Rules
   
4. Data Analyst - A data analyst manages data catalog, data ownership and data encryption/obfuscation rules, with the following permissions:  
   * Manage Data Catalog
   * Edit Data Classification and Encryption
   * Edit Data Ownership
   
5. Database Administrator - A database administrator manages database related operations, with the following permissions:  
   * Manage Backup Jobs
   * Manage Database Deployments
   * Manage Database Operations
   * Schedule Encryption and Obfuscation
   
6. User - A generic user role with no special permission to the system  
7. SecureFile Only - A limited user role that allows access to the SecureFile feature only. If this role is assigned, no other role will be effective even if assigned. 
Users with this role can only access the SecureFile feature and the user profile page on the OnData Portal. Do not assign this role to a user if you expect this 
user to access other features as well  
     
## Create Custom Roles
a. Prerequisite: An OnData account with the System Administrator role  
b. From the Settings menu on the top right corner, select "Organization"  
c. Select the "Users" tab. And on the top right corner, click on "Manage Roles"  
d. The list of existing roles will be displayed. To see the default or built-in roles, click on "Show Built-In Roles"  
e. To create a custom role, click on the "New Custom Role" button. On the pop-up, enter a "Display Name", "Value" and "Description" for the role, and select one or 
more permissions for that role. Please note that the "Value" field cannot contain any whitespaces  
  
![Create Custom Role](${ img0 })  

d. Click on the "Save" button below to create the custom role. The newly created custom role can be assigned to your users  
  
  `
  
export default content;
